import React, { useState, useMemo } from 'react'
import { Permission } from '../types/rolePermission'
import { UserRole } from '../types/user'
import { ALL_PERMISSIONS, getPermissionsByCategory } from '../constants/permissions'
import { PermissionCategory } from '../types/rolePermission'
import './RolePermissionEditor.css'

interface RolePermissionEditorProps {
  role: UserRole
  initialPermissions: Permission[]
  onSave: (permissions: Permission[]) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function RolePermissionEditor({
  role,
  initialPermissions,
  onSave,
  onCancel,
  isLoading,
}: RolePermissionEditorProps) {
  const [selectedPermissions, setSelectedPermissions] = useState<Set<Permission>>(
    new Set(initialPermissions)
  )
  const [hasAllPermissions, setHasAllPermissions] = useState(initialPermissions.includes('*'))

  const permissionsByCategory = useMemo(() => getPermissionsByCategory(), [])

  const handleToggleAllPermissions = (checked: boolean) => {
    setHasAllPermissions(checked)
    if (checked) {
      setSelectedPermissions(new Set(['*']))
    } else {
      setSelectedPermissions(new Set())
    }
  }

  const handleTogglePermission = (permission: Permission, checked: boolean) => {
    if (permission === '*') {
      handleToggleAllPermissions(checked)
      return
    }

    const newSelected = new Set(selectedPermissions)
    if (checked) {
      newSelected.delete('*') // Remove wildcard if selecting specific permissions
      newSelected.add(permission)
      setHasAllPermissions(false)
    } else {
      newSelected.delete(permission)
    }
    setSelectedPermissions(newSelected)
  }

  const handleToggleCategory = (category: PermissionCategory, checked: boolean) => {
    const categoryPermissions = permissionsByCategory[category]
    const newSelected = new Set(selectedPermissions)
    
    // Remove wildcard if selecting specific permissions
    newSelected.delete('*')
    setHasAllPermissions(false)

    if (checked) {
      categoryPermissions.forEach((perm) => {
        newSelected.add(perm.permission)
      })
    } else {
      categoryPermissions.forEach((perm) => {
        newSelected.delete(perm.permission)
      })
    }
    setSelectedPermissions(newSelected)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const permissionsArray = hasAllPermissions ? ['*'] : Array.from(selectedPermissions)
    await onSave(permissionsArray)
  }

  const isCategorySelected = (category: PermissionCategory): boolean => {
    if (hasAllPermissions) return false
    const categoryPermissions = permissionsByCategory[category]
    return categoryPermissions.every((perm) => selectedPermissions.has(perm.permission))
  }

  const isCategoryPartiallySelected = (category: PermissionCategory): boolean => {
    if (hasAllPermissions) return false
    const categoryPermissions = permissionsByCategory[category]
    const selectedCount = categoryPermissions.filter((perm) =>
      selectedPermissions.has(perm.permission)
    ).length
    return selectedCount > 0 && selectedCount < categoryPermissions.length
  }

  const formatRoleName = (role: UserRole): string => {
    return role
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return (
    <form onSubmit={handleSubmit} className="role-permission-editor">
      <div className="editor-header">
        <h2>Edit Permissions: {formatRoleName(role)}</h2>
        <p className="editor-description">
          Select the permissions for this role. Users with this role will have access to the selected
          features.
        </p>
      </div>

      <div className="permissions-container">
        {/* All Permissions Option */}
        <div className="permission-section system-permissions">
          <div className="permission-group-header">
            <label className="permission-checkbox-label">
              <input
                type="checkbox"
                checked={hasAllPermissions}
                onChange={(e) => handleToggleAllPermissions(e.target.checked)}
                className="permission-checkbox"
              />
              <span className="permission-label-text">
                <strong>{ALL_PERMISSIONS.permission}</strong> - {ALL_PERMISSIONS.description}
              </span>
            </label>
          </div>
        </div>

        {/* Permissions by Category */}
        {!hasAllPermissions && (
          <>
            {(Object.keys(permissionsByCategory) as PermissionCategory[]).map((category) => {
              const categoryPermissions = permissionsByCategory[category]
              if (categoryPermissions.length === 0) return null

              const categorySelected = isCategorySelected(category)
              const categoryPartial = isCategoryPartiallySelected(category)

              return (
                <div key={category} className="permission-section">
                  <div className="permission-group-header">
                    <label className="permission-checkbox-label category-label">
                      <input
                        type="checkbox"
                        checked={categorySelected}
                        ref={(input) => {
                          if (input) {
                            input.indeterminate = categoryPartial && !categorySelected
                          }
                        }}
                        onChange={(e) => handleToggleCategory(category, e.target.checked)}
                        className="permission-checkbox"
                      />
                      <span className="permission-label-text category-name">
                        <strong>{category}</strong>
                      </span>
                    </label>
                  </div>
                  <div className="permission-list">
                    {categoryPermissions.map((perm) => (
                      <label key={perm.permission} className="permission-checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedPermissions.has(perm.permission)}
                          onChange={(e) => handleTogglePermission(perm.permission, e.target.checked)}
                          className="permission-checkbox"
                        />
                        <span className="permission-label-text">
                          <code className="permission-code">{perm.permission}</code>
                          <span className="permission-description"> - {perm.description}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )
            })}
          </>
        )}
      </div>

      <div className="editor-actions">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Permissions'}
        </button>
      </div>
    </form>
  )
}
